'use client';

/**
 * WEB port of skillzapp `LiveEvent/LiveEventCarousel`: a snap row of up to 5 live
 * event cards with pagination dots. The RN version uses react-native-snap-carousel
 * + autoplay; on web a CSS scroll-snap row (native, no lib) is the right tool, and
 * the active dot is tracked from scroll position. Dots match the native styles
 * (active 30×10 blue, inactive 10×10 grey).
 */

import {useRef, useState} from 'react';
import type {LiveEvent} from '@/services/partner/getLiveEvents';
import LiveEventCard from './LiveEventCard';

const MAX_ITEMS = 5;

interface LiveEventCarouselProps {
  events: LiveEvent[];
  slug: string;
  rootMode?: boolean;
}

export default function LiveEventCarousel({events, slug, rootMode}: LiveEventCarouselProps) {
  const items = events.slice(0, MAX_ITEMS);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    // Card width incl. gap ≈ scrollWidth / count; index = round(scrollLeft / step).
    const step = el.scrollWidth / items.length;
    setActive(Math.round(el.scrollLeft / step));
  };

  if (!items.length) return null;

  return (
    <div>
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        // Inset the row by exactly one inter-card gap (11px phone / 14px from `sm`) so the
        // space before the first card matches the space between cards and the rhythm reads
        // even — the section itself is full-bleed, so without this the first card sat hard
        // against the screen edge on a phone.
        //
        // `scroll-pl-*` is what makes that padding survive: with `snap-mandatory` +
        // `snap-start` the browser aligns the first card's edge to the scrollport edge,
        // which silently scrolls the padding away (measured: padding-left 16px, first card
        // still at x=0). scroll-padding-left moves the snap line inward by the same amount,
        // so the card actually rests at the inset.
        // `overflow-x-auto` makes overflow-y compute to `auto` too (CSS quirk), so the
        // card's `hover:-translate-y-0.5` lift clips against the top edge on desktop. The
        // pt-* here matches pb-* to give the 2px lift room to breathe.
        className='flex snap-x snap-mandatory gap-[11px] overflow-x-auto scroll-pl-4 px-4 pb-1 pt-1 [scrollbar-width:none] sm:gap-[14px] sm:scroll-pl-11 sm:px-11 sm:pb-1.5 sm:pt-1.5 [&::-webkit-scrollbar]:hidden'
      >
        {items.map((event, i) => (
          <div
            key={`${event.gameId}-${event.startsAt}-${i}`}
            className='w-[266px] shrink-0 snap-start sm:w-[382px]'
          >
            <LiveEventCard event={event} slug={slug} rootMode={rootMode} />
          </div>
        ))}
      </div>
      {items.length > 1 ? (
        // Dots: active 16×4 in the partner accent, idle 4×4 in the card border. Mobile
        // only — on desktop the rail shows multiple cards + a peek and scroll/drags, so a
        // paging dot row (one-card-at-a-time idiom) is dropped (lg:hidden) to match the LTO
        // rail's desktop treatment.
        <div className='flex items-center justify-center gap-1.5 pt-3 lg:hidden'>
          {items.map((_, i) => (
            <span
              key={i}
              className='h-1 rounded-[3px] transition-all'
              style={{
                width: i === active ? 16 : 4,
                backgroundColor: i === active ? 'var(--partner-accent)' : 'var(--partner-card-border)',
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
